import { useCallback } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ContentBlock } from '@/libraries/design/components/content-block/src/content-block';
import { Image } from '@/libraries/design/components/image/src/image';
import {
	WCL_GRID_MAX_WIDTH,
	WCL_GRID_PADDING_LG,
	WCL_GRID_PADDING_SM,
} from '@/libraries/design/foundations/grid/src/base';
import { RichTextComponent } from '@/libraries/design/utils';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

export interface MobileAppProps {
	title: string;
	description?: string | React.ReactElement<typeof RichTextComponent>;
	linkLabel?: string;
	linkHref?: string;
	linkDataEventProps?: DataEventProps;
	imageSrc: string;
	imageAlt?: string;
	appleAppStoreUrl: string;
	googlePlayStoreUrl: string;
}

export const MobileApp = ({
	title,
	description,
	linkLabel,
	linkHref,
	linkDataEventProps,
	imageSrc,
	imageAlt = '',
	appleAppStoreUrl,
	googlePlayStoreUrl,
}: MobileAppProps) => {
	const intl = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleAppStoreClick = useCallback(
		(store: 'apple' | 'google') => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'linkButton',
				actionSubjectId: store === 'apple' ? 'appleAppStore' : 'googlePlayStore',
				source: 'mobileAppSection',
				attributes: {
					label:
						store === 'apple'
							? 'Download on the Apple App Store'
							: 'Get it on the Google Play Store',
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	return (
		<section css={sectionStyles}>
			<div css={leftSideContentStyles}>
				<ContentBlock
					title={title}
					titleAs="h2"
					text={description}
					callToActionProps={
						linkLabel && linkHref
							? {
									link: {
										text: linkLabel,
										href: linkHref,
										...(linkDataEventProps && {
											dataEventProps: linkDataEventProps,
										}),
									},
								}
							: undefined
					}
				/>
				<div css={appStoreLinksContainerStyles}>
					<a href={appleAppStoreUrl} onClick={() => handleAppStoreClick('apple')}>
						<Image
							src="https://dam-cdn.atl.orangelogic.com/AssetLink/3g0xj4162q6vj8iis1nr5pkh2scjm13a.png"
							alt={intl.formatMessage({
								id: 'mobile-app.apple-store-alt',
								defaultMessage: 'Download on the Apple App Store',
							})}
						/>
					</a>
					<a href={googlePlayStoreUrl} onClick={() => handleAppStoreClick('google')}>
						<Image
							src="https://dam-cdn.atl.orangelogic.com/AssetLink/md1w60b88865q56fl84744t5u0heh751.png"
							alt={intl.formatMessage({
								id: 'mobile-app.google-play-alt',
								defaultMessage: 'Get it on the Google Play Store',
							})}
						/>
					</a>
				</div>
			</div>
			<div css={imageContainerStyles}>
				<Image src={imageSrc} alt={imageAlt} css={imageStyles} />
			</div>
		</section>
	);
};

const sectionStyles = css({
	display: 'grid',
	boxSizing: 'border-box',
	gap: 'var(--cl-gap)',
	width: '100%',
	'--cl-gap': '24px',
	margin: 'auto',
	maxWidth: '100%',
	paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
	paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
	[media.only.sm]: {
		paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
		paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
	},
	[media.above.sm]: {
		gridTemplateColumns: '1fr 1fr',
	},
	[media.above.md]: {
		'--cl-gap': '32px',
		paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
		paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
		maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`,
	},
	marginBlockStart: token('space.800'),
});

const leftSideContentStyles = css({
	[media.above.sm]: {
		paddingBlockEnd: token('space.400'),
	},
	[media.above.md]: {
		paddingBlockEnd: token('space.800'),
	},
	marginBlock: 'auto',
});

const appStoreLinksContainerStyles = css({
	display: 'flex',
	gap: token('space.200'),
	marginBlockStart: token('space.400'),
});

const imageContainerStyles = css({
	overflow: 'hidden',
});

const imageStyles = css({
	[media.above.md]: {
		width: '100%',
		height: '420px',
	},
	[media.only.sm]: {
		width: '432px',
		height: 0,
		minHeight: '100%',
	},
	width: '100%',
	height: '263px',
	objectFit: 'cover',
	objectPosition: 'left top',
});
